import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { NotFoundPage } from "@/components/not-found";
import { ReviewedLine } from "@/components/reviewed-line";
import { useEffect } from "react";
import { PageShell, Card } from "@/components/page-shell";
import { Faq, faqJsonLd } from "@/components/faq";
import { CountySnapshot } from "@/components/county-snapshot";
import { SourceList } from "@/components/source-list";
import { ShareCard } from "@/components/share-card";
import { Checklist, InlineLinks } from "@/components/checklist";
import {
  getState,
  getStateByAbbr,
  getNearbyStates,
  countyPagesEnabled,
  type StateInfo,
  type CountySummary,
} from "@/lib/states";
import { riskLabel } from "@/lib/risk";
import { countyLabel, countyNoun, isIndependentCity } from "@/lib/county-label";
import {
  CDC_STATUS_LABEL,
  CDC_STATUS_SOURCE,
  CDC_STATUS_VAR,
  cdcStatusMeaning,
  countyHeadline,
  type CdcCountyStatus,
  type StateCountyStatuses,
} from "@/lib/county-status-text";
import { breadcrumbJsonLd, articleJsonLd, medicalWebPageJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { countyDescription, countyTitle } from "@/lib/seo";
import { LONE_STAR_TICK, placeEntity } from "@/lib/entities";

/** The few county fields this page renders — deliberately not the full dataset row. */
interface CountyRef {
  fips: string;
  name: string;
  slug: string;
  stateAbbr: string;
}

/** A neighbouring county, with its own CDC record so the list doubles as a local key. */
interface NearbyCounty extends CountyRef {
  status: CdcCountyStatus;
}
import { track } from "@/lib/analytics";

/**
 * Find a matching per-county note from the parent state's countyData. Matches on a
 * word boundary so "Lee" doesn't match "Leelanau County" — the countyData name must
 * start with the exact county name followed by end-of-string or a space (e.g.
 * "Pulaski County (Little Rock area)", "Orleans Parish").
 */
function matchCountySummary(state: StateInfo, countyName: string): CountySummary | undefined {
  const target = countyName.toLowerCase();
  return state.countyData?.find((c) => {
    const name = c.name.toLowerCase();
    return name === target || name.startsWith(`${target} `);
  });
}

/**
 * What a county's CDC record means for someone there, said after CDC's definition. The
 * record comes first and the state's picture second: before CDC's county data was used
 * here, every county in an "established" state was told to "expect to meet this tick",
 * including counties CDC has no record for at all.
 */
/** The closest county CDC lists as established, for a county that isn't. */
interface NearestRef {
  label: string;
  stateAbbr: string;
  miles: number;
  /** "county", "parish" or "city": what to call the place named. */
  noun: string;
}

/** "The nearest county CDC lists as established is Polk County, IA, about 40 miles away." */
function nearestSentence(n: NearestRef, from?: string): string {
  return `The nearest ${n.noun} where CDC lists them as established is ${n.label}, ${n.stateAbbr}, about ${n.miles} miles ${from ? `from ${from}` : "away"}, measured between centers.`;
}

function contextCopy(
  state: StateInfo,
  status: CdcCountyStatus,
  nearest: NearestRef | null = null,
): string {
  const near = nearest ? ` ${nearestSentence(nearest)}` : "";
  return contextBase(state, status) + near;
}

function contextBase(state: StateInfo, status: CdcCountyStatus): string {
  const s = state.name;
  switch (status) {
    case "established":
      return "They live in wooded edges, tall grass, brush and leaf litter, so people and pets who spend time outdoors here should expect to meet them in the warmer months.";
    case "reported":
      return "Treat them as present and take the usual steps outdoors.";
    default:
      if (state.risk === "high" || state.risk === "established") {
        return `The tick is established in much of ${s}, and records lag behind where ticks really live.`;
      }
      if (state.risk === "emerging") {
        return `${s} is at the spreading edge of the tick's range, so routine tick checks are still worthwhile.`;
      }
      return "Single ticks can still arrive on wildlife, pets or travel, and other ticks live here, so basic prevention still applies.";
  }
}

/** "March – October" -> "from March to October"; a year-round season says so. */
function seasonPhrase(peakSeason: string): string {
  const months = peakSeason.match(/([A-Z][a-z]+) – ([A-Z][a-z]+)/);
  if (peakSeason.startsWith("Year-round")) {
    return months ? `all year, most of all from ${months[1]} to ${months[2]}` : "all year";
  }
  return months ? `from ${months[1]} to ${months[2]}` : "from April to September, CDC says";
}

function countyFaq(
  c: string,
  stateName: string,
  peakSeason: string,
  status: CdcCountyStatus,
  noun = "county",
  nearest: NearestRef | null = null,
) {
  const near = nearest
    ? [
        {
          q: `How far is ${c} from where Lone Star ticks are established?`,
          a:
            status === "reported"
              ? `Lone Star ticks have been reported in ${c} itself, though not in the numbers CDC counts as established. ${nearestSentence(nearest, c)}`
              : `${nearestSentence(nearest, c)} Single ticks can arrive on wildlife, pets or travel, so a ${noun} with no record can still have them.`,
        },
      ]
    : [];
  return [
    {
      q: `Are there Lone Star ticks in ${c}, ${stateName}?`,
      a: `CDC's ${CDC_STATUS_SOURCE.year} ${noun} record for ${c} says "${CDC_STATUS_LABEL[status]}". ${cdcStatusMeaning(status, noun)} Ticks are most active ${seasonPhrase(peakSeason)}. Finding ticks is not the same as personal disease risk.`,
    },
    ...near,
    {
      q: `When is tick season in ${c}?`,
      a: `In ${stateName}, Lone Star ticks are most active ${seasonPhrase(peakSeason)}. They can still bite on warm days outside those months, so keep up tick checks.`,
    },
    {
      q: `Can a tick bite in ${c} cause alpha-gal syndrome?`,
      a: `It can. Lone Star tick bites are linked to alpha-gal syndrome, a delayed allergy to red meat. Most bites do not cause it, and only a doctor can diagnose it. Watch for hives, swelling or stomach trouble 2 to 6 hours after eating red meat.`,
    },
    {
      q: `What should I do if I find a tick in ${c}?`,
      a: `Remove it with fine-tipped tweezers, pulling straight up with steady pressure, then clean the area. Save a photo, note the date, and watch for symptoms over the following weeks. Contact a licensed clinician if a rash, fever, fatigue, or a delayed reaction to red meat develops.`,
    },
    {
      q: `How do I report or identify a tick found in ${c}?`,
      a: `Save the tick in a sealed bag with a moist paper towel and take a clear photo. Your ${stateName} health department or a university extension program may identify or test it. LoneStarMap does not collect tick reports. Official surveillance runs through those agencies.`,
    },
  ];
}

export const Route = createFileRoute("/states/$state_/$county")({
  // County lookup and the nearby-county graph both run here rather than in the
  // component, behind a dynamic import: src/lib/counties.ts is 362KB and there is no
  // reason for a browser to hold all 3,142 counties to render one page about one.
  loader: async ({
    params,
  }): Promise<{
    state: StateInfo;
    county: CountyRef;
    nearby: NearbyCounty[];
    cdcStatus: CdcCountyStatus;
    stateStatuses: StateCountyStatuses;
    nearest: NearestRef | null;
  }> => {
    const state = getState(params.state);
    if (!state) throw notFound();
    // A state whose county pages are switched off (it moved to "Rarely reported") sends
    // its old county addresses to the state page rather than to a dead end.
    if (!countyPagesEnabled(state)) {
      throw redirect({ href: `/states/${state.slug}`, statusCode: 301 });
    }
    const [
      { getCounty, getCountiesByState, getNearbyCounties },
      { cdcCountyStatus, statusesForState },
      { nearestEstablished },
    ] = await Promise.all([
      import("@/lib/reports"),
      import("@/lib/county-status"),
      import("@/lib/nearest-established"),
    ]);
    const found = getCounty(state.abbr, params.county);
    if (!found) throw notFound();
    const nearby = getNearbyCounties(found, 8, (c) => {
      const st = getStateByAbbr(c.stateAbbr);
      return Boolean(st && countyPagesEnabled(st));
    }).map((c) => ({
      fips: c.fips,
      name: c.name,
      slug: c.slug,
      stateAbbr: c.stateAbbr,
      status: cdcCountyStatus(c.fips),
    }));
    const cdcStatus = cdcCountyStatus(found.fips);
    const near = cdcStatus === "established" ? null : nearestEstablished(found);
    return {
      state,
      county: { fips: found.fips, name: found.name, slug: found.slug, stateAbbr: found.stateAbbr },
      nearby,
      cdcStatus,
      nearest: near
        ? {
            label: countyLabel(near),
            stateAbbr: near.stateAbbr,
            miles: near.miles,
            noun:
              isIndependentCity(near) || near.stateAbbr === "DC"
                ? "city"
                : countyNoun(near.stateAbbr),
          }
        : null,
      // Two short FIPS lists for this state only (Texas: ~180 codes), so the county map
      // can colour every county without the browser loading CDC's national file.
      stateStatuses: statusesForState(getCountiesByState(state.abbr).map((c) => c.fips)),
    };
  },
  head: ({ loaderData }) => {
    const state = loaderData?.state;
    const county = loaderData?.county;
    if (!state || !county) return {};
    const name = countyLabel(county);
    const path = `/states/${state.slug}/${county.slug}`;
    const peak = state.peakSeason;
    const ogImage = ogImageUrl({
      title: `Lone Star ticks in ${name}, ${state.name}`,
      subtitle: peak ? `Peak season: ${peak}` : "Tick & alpha-gal awareness",
      badge: riskLabel[state.risk],
      level: state.risk,
      kind: "state",
      state: state.slug,
      county: county.slug,
    });
    // The snippet answers the search ("are there Lone Star ticks in <county>?") with
    // CDC's record before anyone clicks. src/lib/seo.test.ts runs every county through it.
    const title = countyTitle(name, state.abbr, CDC_STATUS_SOURCE.year);
    const description = countyDescription({
      label: name,
      stateName: state.name,
      status: loaderData.cdcStatus,
      peakSeason: peak,
      level: state.risk,
      year: CDC_STATUS_SOURCE.year,
    });
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: `Lone Star Ticks in ${name}, ${state.name}` },
        { property: "og:description", content: description },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { name: "twitter:title", content: `Lone Star Ticks in ${name}, ${state.name}` },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            faqJsonLd(
              countyFaq(
                name,
                state.name,
                peak,
                loaderData.cdcStatus,
                countyNoun(state.abbr),
                loaderData.nearest,
              ),
            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            medicalWebPageJsonLd({
              name: `Lone Star Ticks in ${name}, ${state.name}`,
              description,
              path,
              lastReviewed: state.lastReviewed,
              image: ogImage,
              about: [LONE_STAR_TICK, placeEntity(`${name}, ${state.name}`, state.name)],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            articleJsonLd({
              headline: `Lone Star Ticks in ${name}, ${state.name}`,
              description,
              path,
              datePublished: state.lastReviewed,
              section: "Tick awareness",
              image: ogImage,
              about: [LONE_STAR_TICK, placeEntity(`${name}, ${state.name}`, state.name)],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "States", path: "/states" },
              { name: state.name, path: `/states/${state.slug}` },
              { name, path },
            ]),
          ),
        },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <PageShell title="Couldn't load this county">
      <p className="text-sm text-muted-foreground">
        {error instanceof Error ? error.message : String(error)}
      </p>

      <Link to="/states" className="text-sm font-semibold text-primary underline">
        Browse all states
      </Link>
    </PageShell>
  ),
  notFoundComponent: () => <NotFoundPage kind="county" />,
  component: CountyPage,
});

function CountyPage() {
  const { state, county, nearby, cdcStatus, stateStatuses, nearest } = Route.useLoaderData();
  const name = countyLabel(county);
  const faqItems = countyFaq(
    name,
    state.name,
    state.peakSeason,
    cdcStatus,
    countyNoun(state.abbr),
    nearest,
  );
  const summary = matchCountySummary(state, county.name);
  const nearbyStates = getNearbyStates(state);

  useEffect(() => {
    track("state_page_viewed", {
      state: state.name,
      slug: `${state.slug}/${county.slug}`,
      risk: state.risk,
      scope: "county",
    });
  }, [state.name, state.slug, state.risk, county.slug]);

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "States", path: "/states" },
        { name: state.name, path: `/states/${state.slug}` },
        { name, path: `/states/${state.slug}/${county.slug}` },
      ]}
      eyebrow={state.name}
      title={`Lone Star ticks in ${name}`}
    >
      <CountySnapshot
        state={state}
        fips={county.fips}
        label={name}
        status={cdcStatus}
        lists={stateStatuses}
        summary={summary}
        context={contextCopy(state, cdcStatus, nearest)}
      />

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          If you find one on you
        </h2>
        <p className="measure mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
          Take it out with fine-tipped tweezers, pulling straight up, and write down the date. The
          step-by-step guide tells you what to watch for, and for how long.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to="/tick-bite-tool"
            className="inline-flex rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
          >
            What to do after a bite
          </Link>
          <Link to="/tick-identification" className="link text-sm font-semibold">
            Identify the tick
          </Link>
        </div>
      </Card>

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          How to avoid bites in {name}
        </h2>
        <Checklist items={state.preventionNotes} className="mt-5" />
        <Link to="/prevention" className="link mt-4 inline-block text-sm font-semibold">
          The full prevention guide
        </Link>
      </Card>

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          Common questions
        </h2>
        <div className="mt-5">
          <Faq items={faqItems} />
        </div>
      </Card>

      <div className="space-y-2">
        <SourceList ids={state.sourceIds} title="Where this comes from" collapsible />
        <ReviewedLine
          iso={state.lastReviewed}
          label="Regional data last reviewed:"
          className="text-xs text-muted-foreground"
        />
      </div>

      <nav aria-labelledby="nearby-places">
        <h2 id="nearby-places" className="label-caps text-muted-foreground">
          Nearby, by CDC&rsquo;s record
        </h2>
        <ul className="mt-3 grid gap-x-10 border-t border-border sm:grid-cols-2">
          {nearby.map((c) => {
            const st = getStateByAbbr(c.stateAbbr);
            if (!st) return null;
            return (
              <li
                key={c.fips}
                className="flex items-baseline justify-between gap-4 border-b border-border py-2"
              >
                <Link
                  to="/states/$state/$county"
                  params={{ state: st.slug, county: c.slug }}
                  className="link min-w-0 text-[0.9375rem]"
                >
                  {countyLabel(c)}
                  {c.stateAbbr !== state.abbr ? `, ${c.stateAbbr}` : ""}
                </Link>
                <span className="inline-flex shrink-0 items-center gap-2 text-sm text-foreground">
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-2.5 ring-1 ring-foreground/25 [print-color-adjust:exact]"
                    style={{ backgroundColor: CDC_STATUS_VAR[c.status] }}
                  />
                  {CDC_STATUS_LABEL[c.status]}
                </span>
              </li>
            );
          })}
        </ul>
        <InlineLinks className="mt-4">
          <li>
            <Link to="/states/$state" params={{ state: state.slug }} className="link font-semibold">
              All of {state.name}
            </Link>
          </li>
          {nearbyStates.map((x) => (
            <li key={x.slug}>
              <Link to="/states/$state" params={{ state: x.slug }} className="link">
                {x.name}
              </Link>
            </li>
          ))}
        </InlineLinks>
      </nav>


      <ShareCard
        title={`Lone Star ticks in ${name}, ${state.name}`}
        subtitle={`Peak season: ${state.peakSeason}`}
        level={state.risk}
        badge={riskLabel[state.risk]}
        kind="state"
        state={state.slug}
        county={county.slug}
        summaryText={`${countyHeadline(name, cdcStatus)} (CDC county record, ${CDC_STATUS_SOURCE.year}: ${CDC_STATUS_LABEL[cdcStatus]})\nAll of ${state.name}: ${riskLabel[state.risk]}. Peak season: ${state.peakSeason}\nhttps://lonestarmap.com/states/${state.slug}/${county.slug}`}
        sharePath={`/states/${state.slug}/${county.slug}`}
        shareText={`Know someone in ${name}, ${state.name} who hikes, hunts, owns a dog or works outdoors? Where Lone Star ticks live, and what to do about them:`}
      />

      <p className="text-xs leading-relaxed text-muted-foreground">
        Educational information only, not medical advice. If someone is struggling to breathe or
        having a severe reaction, call 911. Read the{" "}
        <Link to="/disclaimer" className="link">
          disclaimer
        </Link>
        .
      </p>
      <p className="print-only text-xs">
        Printed from lonestarmap.com/states/{state.slug}/{county.slug}. Check the page for the
        latest version.
      </p>
    </PageShell>
  );
}
